import { useEffect, useState } from 'react'
import type { GccEligibilityRule, GccDispositionTag, GccSourcingTier } from '@shared/types'
import { hasIncludedDisposition, NO_DISPOSITION_SELECTED_MESSAGE } from '@shared/gcc/eligibility'
import { GCC_AUTOMATE_FIRST_LABEL, isValidAutomateFirstThreshold } from '@shared/gcc/sourcing'
import { GCC_QUICK_WIN_HORIZON_MAX_MONTHS, GCC_QUICK_WIN_LABEL, toQuickWinHorizonSliderMonths } from '@shared/gcc/quick-win'
import { GCC_AGENT_CATALOG_UNAVAILABLE_NOTE, GCC_PLANNED_AGENT_SOURCES } from '@shared/gcc/agent-catalog'

const GCC_COLOR = '#059669'

const AVAILABLE_FUNCTIONS = [
  { key: 'HR', label: 'Human Resources', icon: '👥' },
  { key: 'Finance', label: 'Finance', icon: '💰' },
  { key: 'Supply Chain', label: 'Supply Chain', icon: '🚀' },
  { key: 'Technology', label: 'Technology / IT', icon: '💻' },
  { key: 'Marketing', label: 'Marketing', icon: '📣' },
  { key: 'Customer Service', label: 'Customer Service', icon: '🎧' },
  { key: 'Legal', label: 'Legal', icon: '⚖️' },
  { key: 'Operations', label: 'Operations', icon: '⚙️' },
]

const DISPOSITION_OPTIONS: GccDispositionTag[] = ['Automate', 'Agentify', 'Augment', 'Human']
const SOURCING_TIERS: GccSourcingTier[] = ['HQ', 'Markets', 'GCC-Offshore', 'GCC-Nearshore', 'MSP']

function getIncludedDispositions(rules: GccEligibilityRule[]): GccDispositionTag[] {
  const rule = rules.find(r => r.field === 'disposition' && (r.operator === 'in' || r.operator === 'equals'))
  if (!rule) return []
  return (Array.isArray(rule.value) ? rule.value : [rule.value]) as GccDispositionTag[]
}

function getExcludedTiers(rules: GccEligibilityRule[]): GccSourcingTier[] {
  const rule = rules.find(r => r.field === 'sourcingTier' && (r.operator === 'not-in' || r.operator === 'not-equals'))
  if (!rule) return []
  return (Array.isArray(rule.value) ? rule.value : [rule.value]) as GccSourcingTier[]
}

/** Slider readout for the automate-first threshold (WI 2886): 100% applies to no task. */
function automateFirstSliderText(pct: number): string {
  return pct >= 100 ? 'Off (rule not applied)' : `${pct}%`
}

/** Slider readout for the quick-win horizon (WI 2886): 0 turns quick wins off. */
function quickWinHorizonSliderText(months: number): string {
  return months === 0 ? 'Off' : `${months} month${months === 1 ? '' : 's'}`
}

function buildRules(dispositions: GccDispositionTag[], excludedTiers: GccSourcingTier[]): GccEligibilityRule[] {
  const rules: GccEligibilityRule[] = []
  if (dispositions.length > 0) {
    rules.push({ field: 'disposition', operator: 'in', value: dispositions })
  }
  if (excludedTiers.length === 1) {
    rules.push({ field: 'sourcingTier', operator: 'not-equals', value: excludedTiers[0] })
  } else if (excludedTiers.length > 1) {
    rules.push({ field: 'sourcingTier', operator: 'not-in', value: excludedTiers })
  }
  return rules
}

interface Props {
  functionScope: string[]; setFunctionScope: (v: string[]) => void
  eligibilityRules: GccEligibilityRule[]
  setEligibilityRules: (r: GccEligibilityRule[]) => void
  /** Whole percent 1–100; an invalid value blocks Next */
  automateFirstThresholdPct: number
  setAutomateFirstThresholdPct: (v: number) => void
  /** Whole months 0–6 (0 = off); any other value is replaced with the slider's position (WI 2886) */
  quickWinHorizonMonths: number
  setQuickWinHorizonMonths: (v: number) => void
  onBack: () => void
  onNext: () => void
}

export default function GccStep2_Config({
  functionScope, setFunctionScope,
  eligibilityRules, setEligibilityRules,
  automateFirstThresholdPct, setAutomateFirstThresholdPct,
  quickWinHorizonMonths, setQuickWinHorizonMonths,
  onBack, onNext,
}: Props) {
  // true = Full Enterprise selected (functionScope = [])
  const [isFullEnterprise, setIsFullEnterprise] = useState(functionScope.length === 0)

  const includedDispositions = getIncludedDispositions(eligibilityRules)
  const excludedTiers = getExcludedTiers(eligibilityRules)
  const thresholdValid = isValidAutomateFirstThreshold(automateFirstThresholdPct)
  // A horizon outside 0–6 (e.g. one saved before WI 2886) becomes the slider's position, so the run uses what it shows
  const horizon = toQuickWinHorizonSliderMonths(quickWinHorizonMonths)
  useEffect(() => {
    if (horizon !== quickWinHorizonMonths) setQuickWinHorizonMonths(horizon)
  }, [horizon, quickWinHorizonMonths, setQuickWinHorizonMonths])
  const canProceed = hasIncludedDisposition(eligibilityRules) && thresholdValid

  const toggleFullEnterprise = () => {
    setIsFullEnterprise(true)
    setFunctionScope([])
  }

  const toggleFunction = (key: string) => {
    const current = isFullEnterprise ? [] : functionScope
    const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key]
    if (next.length === 0) {
      setIsFullEnterprise(true)
      setFunctionScope([])
    } else {
      setIsFullEnterprise(false)
      setFunctionScope(next)
    }
  }

  const toggleDisposition = (d: GccDispositionTag) => {
    const next = includedDispositions.includes(d)
      ? includedDispositions.filter(x => x !== d)
      : [...includedDispositions, d]
    setEligibilityRules(buildRules(next, excludedTiers))
  }

  const toggleTier = (t: GccSourcingTier) => {
    const next = excludedTiers.includes(t)
      ? excludedTiers.filter(x => x !== t)
      : [...excludedTiers, t]
    setEligibilityRules(buildRules(includedDispositions, next))
  }

  return (
    <div className="max-w-2xl mx-auto px-8 py-8 space-y-8">
      {/* Section header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">TN Global Design Configuration</h2>
        <p className="text-sm text-gray-500 mt-1">
          Define which functions to analyse and set eligibility pre-processing rules.
        </p>
      </div>

      {/* Function Scope */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Function Scope</h3>

        {/* Full Enterprise card */}
        <button
          type="button"
          onClick={toggleFullEnterprise}
          className={`w-full flex items-center justify-between gap-3 p-4 rounded-xl border-2 text-left mb-3 transition-all ${
            isFullEnterprise
              ? 'border-emerald-500 bg-emerald-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg flex-shrink-0 ${
              isFullEnterprise ? 'bg-emerald-100' : 'bg-gray-100'
            }`}>
              🏢
            </div>
            <div>
              <div className={`text-sm font-semibold ${isFullEnterprise ? 'text-emerald-800' : 'text-gray-800'}`}>
                Full Enterprise / Entire Organisation
              </div>
              <div className="text-xs text-gray-500">AI identifies all business functions relevant to this company</div>
            </div>
          </div>
          {isFullEnterprise && (
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>

        {/* Individual functions grid */}
        <div className="grid grid-cols-2 gap-2">
          {AVAILABLE_FUNCTIONS.map(fn => {
            const selected = !isFullEnterprise && functionScope.includes(fn.key)
            return (
              <button
                key={fn.key}
                type="button"
                onClick={() => toggleFunction(fn.key)}
                className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all text-left ${
                  selected
                    ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className="text-base">{fn.icon}</span>
                <span>{fn.label}</span>
              </button>
            )
          })}
        </div>
      </section>

      {/* Eligibility Rules */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Eligibility Rules</h3>
        <p className="text-xs text-gray-500 mb-4">
          Tasks must meet all rules to be GCC-eligible. These defaults follow standard global capability design methodology.
        </p>

        {/* Disposition include */}
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-600 mb-2">Include tasks with disposition:</p>
          {/* Chips are toggles: aria-pressed tells assistive tech (and TNTAF) which are on */}
          <div role="group" aria-label="Include tasks with disposition" className="flex flex-wrap gap-2">
            {DISPOSITION_OPTIONS.map(d => {
              const selected = includedDispositions.includes(d)
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleDisposition(d)}
                  className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all ${
                    selected
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {d}
                </button>
              )
            })}
          </div>
          {!hasIncludedDisposition(eligibilityRules) && (
            <p id="gcc-disposition-error" role="alert" className="text-xs text-red-600 mt-2">
              {NO_DISPOSITION_SELECTED_MESSAGE}
            </p>
          )}
        </div>

        {/* Sourcing tier exclude */}
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Exclude tasks sourced from:</p>
          <div role="group" aria-label="Exclude tasks sourced from" className="flex flex-wrap gap-2">
            {SOURCING_TIERS.map(t => {
              const selected = excludedTiers.includes(t)
              return (
                <button
                  key={t}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleTier(t)}
                  className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all ${
                    selected
                      ? 'border-amber-400 bg-amber-50 text-amber-700'
                      : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {t}
                </button>
              )
            })}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Typically HQ roles are retained in the headquarters and excluded from GCC scope.
          </p>
        </div>
      </section>

      {/* Automate first (WI 2048) */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Automate First</h3>
        <p className="text-xs text-gray-500 mb-3">
          Work that AI will largely remove is not migrated. A task whose AI disposition releases at least this share of
          its work is marked &ldquo;{GCC_AUTOMATE_FIRST_LABEL}&rdquo; and its remaining work stays Retained where it is today.
        </p>
        <label className="flex items-center gap-3 text-xs font-medium text-gray-600">
          <span className="w-20">Threshold</span>
          <input
            type="range"
            min={1}
            max={100}
            step={1}
            value={automateFirstThresholdPct}
            onChange={e => setAutomateFirstThresholdPct(Number(e.target.value))}
            aria-valuetext={automateFirstSliderText(automateFirstThresholdPct)}
            aria-invalid={!thresholdValid}
            aria-describedby={thresholdValid ? 'gcc-threshold-hint' : 'gcc-threshold-error'}
            className="flex-1 accent-emerald-600"
          />
          <span data-testid="gcc-threshold-value" className="w-40 text-sm font-semibold text-gray-800">
            {automateFirstSliderText(automateFirstThresholdPct)}
          </span>
        </label>
        {!thresholdValid && (
          <p id="gcc-threshold-error" role="alert" className="text-xs text-red-600 mt-2">
            Choose a whole percent from 1 to 100.
          </p>
        )}
        <p id="gcc-threshold-hint" className="text-xs text-gray-400 mt-2">
          Share of a task&rsquo;s work released by AI. With the default residual assumptions, Automate releases 88% of a task
          and Agentify 68%. 100% turns the rule off.
        </p>
      </section>

      {/* Quick win, agentify first (WI 2806) */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Quick Wins</h3>
        <p className="text-xs text-gray-500 mb-3">
          An Automate or Agentify task that is low complexity and can be live within this horizon is marked
          &ldquo;{GCC_QUICK_WIN_LABEL}&rdquo;. Its AI transformation is planned no later than that, and before any of its work moves.
        </p>
        <label className="flex items-center gap-3 text-xs font-medium text-gray-600">
          <span className="w-20">Horizon</span>
          <input
            type="range"
            min={0}
            max={GCC_QUICK_WIN_HORIZON_MAX_MONTHS}
            step={1}
            value={horizon}
            onChange={e => setQuickWinHorizonMonths(Number(e.target.value))}
            aria-valuetext={quickWinHorizonSliderText(horizon)}
            aria-describedby="gcc-horizon-hint"
            className="flex-1 accent-emerald-600"
          />
          <span data-testid="gcc-horizon-value" className="w-40 text-sm font-semibold text-gray-800">
            {quickWinHorizonSliderText(horizon)}
          </span>
        </label>
        <p id="gcc-horizon-hint" className="text-xs text-gray-400 mt-2">
          Months to implement, from 0 to {GCC_QUICK_WIN_HORIZON_MAX_MONTHS}. 0 turns quick wins off. Complements Automate First:
          a quick win is judged on how soon and how easily it can be agentified, not on the share of work AI releases.
        </p>
      </section>

      {/* Agent catalog placeholder (WI 2050): no catalog exists yet */}
      <section aria-labelledby="gcc-agent-catalog-heading">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h3 id="gcc-agent-catalog-heading" className="text-sm font-semibold text-gray-700">Agent Catalog</h3>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-200 text-gray-500 px-2 py-0.5 rounded">
            COMING SOON
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Ground automation recommendations in deployed Accenture agents by choosing the catalog sources that fit this client.
        </p>
        <div className="flex flex-wrap gap-2 opacity-50 cursor-not-allowed select-none">
          {GCC_PLANNED_AGENT_SOURCES.map(source => (
            <button
              key={source}
              type="button"
              disabled
              aria-pressed={false}
              className="px-3 py-1.5 text-xs rounded-full border border-gray-200 bg-white text-gray-500 font-medium cursor-not-allowed"
            >
              {source}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">{GCC_AGENT_CATALOG_UNAVAILABLE_NOTE}</p>
      </section>

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-100">
        <button
          onClick={onBack}
          className="px-6 py-2.5 rounded-lg text-gray-600 font-semibold text-sm border border-gray-200 hover:bg-gray-50 transition-all"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          aria-describedby={canProceed ? undefined : !thresholdValid ? 'gcc-threshold-error' : 'gcc-disposition-error'}
          className="px-6 py-2.5 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: GCC_COLOR }}
        >
          Next: Generate →
        </button>
      </div>
    </div>
  )
}
